
import React, { useState, useEffect, Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { Bloom, EffectComposer, Noise, Vignette } from '@react-three/postprocessing';
import RubiksCube from '../components/Cube';
import Starfield from '../components/Starfield';

// Fix for Three.js intrinsic elements
const Color = 'color' as any;
const AmbientLight = 'ambientLight' as any;
const PointLight = 'pointLight' as any;

const TITLES = ["RADIANT", "SOFTWARE ENGINEER","THE BUILDER"];

const Home: React.FC = () => {
  const [titleIndex, setTitleIndex] = useState(0);
  const [isChanging, setIsChanging] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const interval = setInterval(() => {
      setIsChanging(true);
      setTimeout(() => {
        setTitleIndex((prev) => (prev + 1) % TITLES.length);
        setIsChanging(false);
      }, 500);
    }, 4500);

    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return (
    <div className="relative w-full h-screen overflow-hidden bg-[#000205]">
      {/* Background Ambience */}
      <div className="absolute inset-0 z-0 bg-[radial-gradient(circle_at_center,_rgba(10,0,30,0.4)_0%,_rgba(0,0,0,1)_100%)] pointer-events-none" />

      {/* Three.js Background Layer */}
      <div className="absolute inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 8], fov: 45 }} dpr={[1, 2]}>
          <Color attach="background" args={['#000205']} />
          <AmbientLight intensity={0.4} />
          <PointLight position={[10, 10, 10]} intensity={1} color="#ffffff" />
          
          <Suspense fallback={null}>
            <RubiksCube />
            <Starfield />
            <EffectComposer enableNormalPass={false}>
              <Bloom 
                intensity={1.5} 
                luminanceThreshold={0.4}
                luminanceSmoothing={0.8} 
                mipmapBlur
              />
              <Noise opacity={0.04} />
              <Vignette eskil={false} offset={0.1} darkness={1.1} />
            </EffectComposer>
          </Suspense>
        </Canvas>
      </div>

      {/* Cyberpunk Hero Text Overlay */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none px-4 text-center">
        <div className="relative max-w-5xl w-full flex flex-col items-center">
          
          {/* Main Glitch Title */}
          <div className="relative group">
            {/* Background scanner line effect */}
            <div className="absolute -inset-x-20 h-[1px] bg-cyan-500/20 top-1/2 animate-[scan_4s_linear_infinite]" />
            
            <h1 className={`relative text-7xl md:text-[10rem] font-black tracking-tighter transition-all duration-300 ${isChanging ? 'opacity-0 scale-95 blur-sm' : 'opacity-100 scale-100 blur-0'}`}>
              {/* Layered Glitch Text */}
              <span className="absolute inset-0 text-magenta-500 mix-blend-screen opacity-70 animate-[glitch-1_2s_infinite] select-none text-[#ff00ff]">
                {TITLES[titleIndex]}
              </span>
              <span className="absolute inset-0 text-cyan-500 mix-blend-screen opacity-70 animate-[glitch-2_2s_infinite] select-none text-[#00ffff]">
                {TITLES[titleIndex]}
              </span>
              <span className="relative text-white z-10 animate-[flicker_5s_infinite] drop-shadow-[0_0_15px_rgba(255,255,255,0.3)]">
                {TITLES[titleIndex]}
              </span>
            </h1>
          </div>
          
          <div className={`mt-12 transition-all duration-700 delay-300 ${isChanging ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
            <div className="inline-flex items-center gap-4 px-6 py-2 border-y border-cyan-500/30 bg-cyan-950/10 backdrop-blur-sm mb-8">
              <span className="w-2 h-2 bg-cyan-500 animate-pulse rounded-full" />
              <p className="text-cyan-400 font-mono font-bold tracking-[0.4em] uppercase text-xs md:text-sm">
                PROTOCOL: IMMERSIVE_UX_v2.5
              </p>
              <span className="w-2 h-2 bg-cyan-500 animate-pulse rounded-full" />
            </div>
            
            <div className="max-w-xl mx-auto space-y-4">
              <p className="text-slate-300 text-lg md:text-2xl font-light tracking-tight px-6" 
                 style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                DECODING THE FUTURE OF <span className="text-white font-medium border-b border-cyan-500/50">INTERACTIVE ART</span> THROUGH CODE.
              </p>
            </div>
          </div>

          {/* Background Ambient Glows */}
          <div className="absolute top-1/2 left-1/4 w-[40%] h-[40%] bg-magenta-600/10 -z-10 blur-[120px] pointer-events-none rounded-full" />
          <div className="absolute bottom-1/2 right-1/4 w-[40%] h-[40%] bg-cyan-600/10 -z-10 blur-[120px] pointer-events-none rounded-full" />
        </div>
      </div>

      {/* Cyberpunk UI Accents */}
      <div className="absolute top-1/2 right-10 -translate-y-1/2 hidden xl:flex flex-col gap-8 opacity-40">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="w-1 h-12 bg-slate-800 rounded-full overflow-hidden">
            <div className="w-full h-full bg-cyan-500 animate-[loading_2s_infinite]" style={{ animationDelay: `${i * 0.2}s` }} />
          </div>
        ))}
      </div>

      <div className="absolute bottom-10 left-10 md:bottom-20 md:left-20 hidden lg:flex items-center gap-4">
        <div className="h-[2px] w-24 bg-gradient-to-r from-cyan-500 via-white to-transparent" />
        <span className="text-[10px] font-mono text-cyan-500 tracking-widest uppercase">System Operational</span>
      </div>
      
      <style>{`
        @keyframes scan {
          0% { transform: translateY(-300px); opacity: 0; }
          50% { opacity: 0.5; }
          100% { transform: translateY(300px); opacity: 0; }
        }
        @keyframes loading {
          0% { transform: translateY(100%); }
          100% { transform: translateY(-100%); }
        }
        @keyframes flicker {
          0%, 19.999%, 22%, 62.999%, 64%, 64.999%, 70%, 100% { opacity: 1; }
          20%, 21.999%, 63%, 63.999%, 65%, 69.999% { opacity: 0.4; }
        }
        @keyframes glitch-1 {
          0% { transform: translate(0); }
          20% { transform: translate(-3px, 3px); }
          40% { transform: translate(-3px, -3px); }
          60% { transform: translate(3px, 3px); }
          80% { transform: translate(3px, -3px); }
          100% { transform: translate(0); }
        }
        @keyframes glitch-2 {
          0% { transform: translate(0); }
          20% { transform: translate(3px, -3px); }
          40% { transform: translate(3px, 3px); }
          60% { transform: translate(-3px, -3px); }
          80% { transform: translate(-3px, 3px); }
          100% { transform: translate(0); }
        }
      `}</style>
    </div>
  );
};

export default Home;
